import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react'
import * as echarts from 'echarts'
import { Box } from '../../atoms/Box'
import { Text } from '../../atoms/Text'
import { Loading } from '../../atoms/Loading'
import { Button } from '../../atoms/Button'
import { ButtonGroup } from '../../molecules/ButtonGroup'
import { StatsContainer } from '../../molecules/StatsContainer'
import {
  ChartContainer,
  ChartSection,
  ChartTitle,
  ErrorContainer,
  NotAvailable,
  PrintWrapper,
} from '../../molecules/ChartComponents'
import {
  generateInsights,
  getChartColors,
  formatShortPricePerSqm,
  formatPricePerSqm,
  calculateKPIs,
  processCityData,
} from './CityAnalysisChart.utils'
import {
  CityAnalysisChartProps,
  CityAnalysisChartRef,
  CityData,
  ViewType,
} from './CityAnalysisChart.types'
import {
  CityAnalysisPrintWrapper,
  ColoredCell,
  DataTableWrapper,
} from './CityAnalysisChart.style'
import { DataTable, DataTableColumn, SortConfig } from '../DataTable'

const VIEW_TYPES: { type: ViewType; label: string }[] = [
  { type: 'price', label: 'By Median Price' },
  { type: 'pricePerSqm', label: 'By Price/m²' },
]

export const CityAnalysisChart = forwardRef<
  CityAnalysisChartRef,
  CityAnalysisChartProps
>(
  (
    {
      data = { cities: [], dealType: '', timestamp: '' },
      loading = false,
      error = null,
      showInsights = true,
      showControls = false,
      isRental = false,
      accentColor = '#00ade6',
      t = (key: string) => key,
    },
    ref
  ) => {
    const [viewType, setViewType] = useState<ViewType>('pricePerSqm')
    const [sortConfig, setSortConfig] = useState<SortConfig[]>([
      { field: 'city', order: 'asc' },
    ])
    const chartRef = useRef<HTMLDivElement>(null)
    const chartInstance = useRef<echarts.ECharts | null>(null)

    // Helper function to get color based on value position in range
    const getValueColor = (
      value: number,
      min: number,
      max: number
    ): { backgroundColor: string; textColor: string } => {
      if (min === max)
        return { backgroundColor: '#f3f4f6', textColor: '#374151' }

      const position = (value - min) / (max - min)

      // Color scale from green (low) to yellow (mid) to red (high)
      if (position <= 0.33) {
        // Green range
        const intensity = position / 0.33
        return {
          backgroundColor: `rgba(34, 197, 94, ${0.1 + intensity * 0.2})`,
          textColor: '#166534',
        }
      } else if (position <= 0.67) {
        // Yellow range
        const intensity = (position - 0.33) / 0.34
        return {
          backgroundColor: `rgba(250, 204, 21, ${0.1 + intensity * 0.2})`,
          textColor: '#854d0e',
        }
      } else {
        // Red range
        const intensity = (position - 0.67) / 0.33
        return {
          backgroundColor: `rgba(239, 68, 68, ${0.1 + intensity * 0.2})`,
          textColor: '#991b1b',
        }
      }
    }

    // Helper function to get pastel color based on value position in range (RGB format)
    const getValuePastelColor = (
      value: number,
      min: number,
      max: number
    ): { backgroundColor: string; textColor: string } => {
      if (min === max)
        return { backgroundColor: '#f3f4f6', textColor: '#374151' }

      const position = (value - min) / (max - min)

      // Color scale from green (low) to yellow (mid) to red (high)
      // Convert RGBA to pastel RGB by blending with white
      if (position <= 0.33) {
        // Green range - blend rgba(34, 197, 94, alpha) with white to create pastel
        const intensity = position / 0.33
        const alpha = 0.1 + intensity * 0.2
        const r = Math.round(34 * alpha + 255 * (1 - alpha))
        const g = Math.round(197 * alpha + 255 * (1 - alpha))
        const b = Math.round(94 * alpha + 255 * (1 - alpha))
        return {
          backgroundColor: `rgb(${r}, ${g}, ${b})`,
          textColor: '#166534',
        }
      } else if (position <= 0.67) {
        // Yellow range - blend rgba(250, 204, 21, alpha) with white to create pastel
        const intensity = (position - 0.33) / 0.34
        const alpha = 0.1 + intensity * 0.2
        const r = Math.round(250 * alpha + 255 * (1 - alpha))
        const g = Math.round(204 * alpha + 255 * (1 - alpha))
        const b = Math.round(21 * alpha + 255 * (1 - alpha))
        return {
          backgroundColor: `rgb(${r}, ${g}, ${b})`,
          textColor: '#854d0e',
        }
      } else {
        // Red range - blend rgba(239, 68, 68, alpha) with white to create pastel
        const intensity = (position - 0.67) / 0.33
        const alpha = 0.1 + intensity * 0.2
        const r = Math.round(239 * alpha + 255 * (1 - alpha))
        const g = Math.round(68 * alpha + 255 * (1 - alpha))
        const b = Math.round(68 * alpha + 255 * (1 - alpha))
        return {
          backgroundColor: `rgb(${r}, ${g}, ${b})`,
          textColor: '#991b1b',
        }
      }
    }

    // Calculate min/max for each numeric column
    const getColumnStats = () => {
      if (!data || processedData.cities.length === 0) {
        return {
          median: { min: 0, max: 0 },
          p25: { min: 0, max: 0 },
          p75: { min: 0, max: 0 },
          count: { min: 0, max: 0 },
          std: { min: 0, max: 0 },
        }
      }

      const cities = processedData.cities
      return {
        median: {
          min: Math.min(...cities.map((c) => c.percentiles.p50)),
          max: Math.max(...cities.map((c) => c.percentiles.p50)),
        },
        p25: {
          min: Math.min(...cities.map((c) => c.percentiles.p25)),
          max: Math.max(...cities.map((c) => c.percentiles.p25)),
        },
        p75: {
          min: Math.min(...cities.map((c) => c.percentiles.p75)),
          max: Math.max(...cities.map((c) => c.percentiles.p75)),
        },
        count: {
          min: Math.min(...cities.map((c) => c.count)),
          max: Math.max(...cities.map((c) => c.count)),
        },
        std: {
          min: Math.min(...cities.map((c) => c.stats.std_deviation)),
          max: Math.max(...cities.map((c) => c.stats.std_deviation)),
        },
      }
    }

    // Define table columns
    const getTableColumns = (): DataTableColumn<CityData>[] => {
      const stats = getColumnStats()

      return [
        {
          key: 'city',
          label: 'City',
          sortable: true,
          render: (item) => (
            <Text variant="body1" style={{ fontWeight: 600 }}>
              {item.city}
            </Text>
          ),
        },
        {
          key: 'median',
          label: 'Median CHF/m²',
          sortable: true,
          render: (item) => {
            const value = item.percentiles.p50
            const colors = getValueColor(
              value,
              stats.median.min,
              stats.median.max
            )
            return (
              <ColoredCell
                $backgroundColor={colors.backgroundColor}
                $textColor={colors.textColor}
              >
                {formatPricePerSqm(value).replace('/m²', '')}
              </ColoredCell>
            )
          },
        },
        {
          key: 'p25',
          label: '25th Percentile',
          sortable: true,
          render: (item) => {
            const value = item.percentiles.p25
            const colors = getValueColor(value, stats.p25.min, stats.p25.max)
            return (
              <ColoredCell
                $backgroundColor={colors.backgroundColor}
                $textColor={colors.textColor}
              >
                {formatPricePerSqm(value).replace('/m²', '')}
              </ColoredCell>
            )
          },
        },
        {
          key: 'p75',
          label: '75th Percentile',
          sortable: true,
          render: (item) => {
            const value = item.percentiles.p75
            const colors = getValueColor(value, stats.p75.min, stats.p75.max)
            return (
              <ColoredCell
                $backgroundColor={colors.backgroundColor}
                $textColor={colors.textColor}
              >
                {formatPricePerSqm(value).replace('/m²', '')}
              </ColoredCell>
            )
          },
        },
        {
          key: 'count',
          label: 'Listings',
          sortable: true,
          render: (item) => {
            const value = item.count
            const colors = getValueColor(
              value,
              stats.count.min,
              stats.count.max
            )
            return (
              <ColoredCell
                $backgroundColor={colors.backgroundColor}
                $textColor={colors.textColor}
              >
                {value.toLocaleString()}
              </ColoredCell>
            )
          },
        },
        {
          key: 'std',
          label: 'Std Deviation',
          sortable: true,
          render: (item) => {
            const value = item.stats.std_deviation
            const colors = getValueColor(value, stats.std.min, stats.std.max)
            return (
              <ColoredCell
                $backgroundColor={colors.backgroundColor}
                $textColor={colors.textColor}
              >
                {formatPricePerSqm(value).replace('/m²', '')}
              </ColoredCell>
            )
          },
        },
      ]
    }

    // Handle sorting
    const handleSort = (field: string) => {
      setSortConfig((currentSort) => {
        // Find current sort config for this field
        const currentFieldSort = currentSort.find((s) => s.field === field)

        if (!currentFieldSort) {
          // No current sort, start with ascending
          return [{ field, order: 'asc' }]
        } else if (currentFieldSort.order === 'asc') {
          // Currently ascending, switch to descending
          return [{ field, order: 'desc' }]
        } else {
          // Currently descending, clear sort
          return []
        }
      })
    }

    // Sort data based on current sort config
    const getSortedData = () => {
      if (!processedData) return []
      if (sortConfig.length === 0) return processedData.cities

      const sorted = [...processedData.cities].sort((a, b) => {
        const { field, order } = sortConfig[0]
        let aVal: any = a
        let bVal: any = b

        switch (field) {
          case 'city':
            aVal = a.city
            bVal = b.city
            break
          case 'median':
            aVal = a.percentiles.p50
            bVal = b.percentiles.p50
            break
          case 'p25':
            aVal = a.percentiles.p25
            bVal = b.percentiles.p25
            break
          case 'p75':
            aVal = a.percentiles.p75
            bVal = b.percentiles.p75
            break
          case 'count':
            aVal = a.count
            bVal = b.count
            break
          case 'std':
            aVal = a.stats.std_deviation
            bVal = b.stats.std_deviation
            break
        }

        if (typeof aVal === 'string') {
          return order === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal)
        }

        return order === 'asc' ? aVal - bVal : bVal - aVal
      })

      return sorted
    }

    useEffect(() => {
      if (!loading && !error && processedData.cities.length > 0) {
        createChart()
      }

      return () => {
        chartInstance.current?.dispose()
      }
    }, [data, viewType, loading, error])

    const processedData = processCityData(data)

    const createChart = () => {
      if (!chartRef.current) return

      if (chartInstance.current) {
        chartInstance.current.dispose()
      }

      chartInstance.current = echarts.init(chartRef.current)

      const colors = getChartColors()

      // Sort cities by name for consistent X-axis ordering
      const sortedCities = [...processedData.cities].sort((a, b) =>
        a.city.localeCompare(b.city)
      )

      const cities = sortedCities.map((c) => c.city)
      const boxData = sortedCities.map((city) => [
        city.percentiles.p10,
        city.percentiles.p25,
        city.percentiles.p50,
        city.percentiles.p75,
        city.percentiles.p90,
      ])

      const option: any = {
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          borderRadius: 8,
          axisPointer: {
            type: 'shadow',
          },
          formatter: (params: any) => {
            if (params[0]) {
              const cityIndex = params[0].dataIndex
              const city = sortedCities[cityIndex]
              return `
              <div style="padding: 12px;">
                <strong style="font-size: 14px;">${city.city}</strong><br/>
                <div style="margin-top: 8px; display: grid; gap: 4px; font-size: 12px;">
                  <div>P90: ${formatPricePerSqm(city.percentiles.p90)}</div>
                  <div>Q3: ${formatPricePerSqm(city.percentiles.p75)}</div>
                  <div style="font-weight: 600;">Median: ${formatPricePerSqm(city.percentiles.p50)}</div>
                  <div>Q1: ${formatPricePerSqm(city.percentiles.p25)}</div>
                  <div>P10: ${formatPricePerSqm(city.percentiles.p10)}</div>
                  <div style="margin-top: 4px; color: #6c757d;">Listings: ${city.count.toLocaleString()}</div>
                </div>
              </div>
            `
            }
            return ''
          },
        },
        grid: {
          left: 80,
          right: 40,
          bottom: 120,
          top: 60,
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: cities,
          axisLabel: {
            rotate: 45,
            fontSize: 12,
            color: '#6c757d',
          },
          axisLine: {
            lineStyle: {
              color: '#e5e7eb',
            },
          },
        },
        yAxis: {
          type: 'value',
          name: 'CHF per m²',
          nameLocation: 'middle',
          nameGap: 50,
          nameTextStyle: {
            fontSize: 14,
            fontWeight: 500,
            color: '#6c757d',
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
          axisLabel: {
            formatter: (value: number) => formatShortPricePerSqm(value),
            color: '#6c757d',
          },
          splitLine: {
            lineStyle: {
              color: '#f0f0f0',
            },
          },
        },
        series: [
          {
            type: 'boxplot',
            data: boxData,
            itemStyle: {
              color: colors.primaryLight,
              borderColor: colors.primary,
              borderWidth: 2,
            },
            emphasis: {
              itemStyle: {
                borderWidth: 3,
                shadowBlur: 10,
                shadowColor: 'rgba(0, 173, 230, 0.4)', // #00ade6 with 0.4 opacity
              },
            },
          },
        ],
      }

      chartInstance.current.setOption(option)
    }

    // Handle window resize
    useEffect(() => {
      const handleResize = () => {
        chartInstance.current?.resize()
      }

      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
    }, [])

    // Expose export methods via ref
    useImperativeHandle(ref, () => ({
      exportChart: () => {
        if (chartInstance.current) {
          chartInstance.current.resize()
          return chartInstance.current.getDataURL({
            type: 'png',
            pixelRatio: 3,
            backgroundColor: '#fff',
            excludeComponents: ['toolbox', 'dataZoom'],
          })
        }
        return null
      },
      getKPIs: () => {
        const kpis = calculateKPIs(processedData)
        return [
          {
            id: 'total-cities',
            label: 'Cities Analyzed',
            value: kpis.totalCities.toString(),
            variant: 'default',
          },
          {
            id: 'avg-price-sqm',
            label: isRental ? 'Average Rent/m²' : 'Average Price/m²',
            value: formatPricePerSqm(kpis.avgPricePerSqm),
            variant: 'default',
          },
          {
            id: 'highest-city',
            label: 'Most Expensive City',
            value: kpis.highestCity ? kpis.highestCity.city : 'N/A',
            variant: 'default',
          },
          {
            id: 'lowest-city',
            label: 'Most Affordable City',
            value: kpis.lowestCity ? kpis.lowestCity.city : 'N/A',
            variant: 'default',
          },
        ]
      },
      getInsights: () => (showInsights ? generateInsights(processedData, t) : []),
      getTableData: () => {
        const stats = getColumnStats()
        const sortedData = getSortedData()

        return sortedData.map((city) => ({
          city: {
            value: city.city,
            backgroundColor: 'transparent',
            textColor: 'inherit',
          },
          median: {
            value: formatPricePerSqm(city.percentiles.p50).replace('/m²', ''),
            ...getValuePastelColor(
              city.percentiles.p50,
              stats.median.min,
              stats.median.max
            ),
          },
          p25: {
            value: formatPricePerSqm(city.percentiles.p25).replace('/m²', ''),
            ...getValuePastelColor(
              city.percentiles.p25,
              stats.p25.min,
              stats.p25.max
            ),
          },
          p75: {
            value: formatPricePerSqm(city.percentiles.p75).replace('/m²', ''),
            ...getValuePastelColor(
              city.percentiles.p75,
              stats.p75.min,
              stats.p75.max
            ),
          },
          count: {
            value: city.count.toLocaleString(),
            ...getValuePastelColor(
              city.count,
              stats.count.min,
              stats.count.max
            ),
          },
          std: {
            value: formatPricePerSqm(city.stats.std_deviation).replace(
              '/m²',
              ''
            ),
            ...getValuePastelColor(
              city.stats.std_deviation,
              stats.std.min,
              stats.std.max
            ),
          },
        }))
      },
    }))

    if (loading) {
      return (
        <Box style={{ textAlign: 'center', padding: '40px' }}>
          <Loading variant="spinner" size="large" center />
          <Text variant="body1" color="secondary" style={{ marginTop: '16px' }}>
            Loading city analysis data...
          </Text>
        </Box>
      )
    }

    if (error) {
      return (
        <ErrorContainer variant="error">
          <Text variant="body1">{error}</Text>
        </ErrorContainer>
      )
    }

    if (processedData.cities.length === 0) {
      return (
        <NotAvailable
          title="No Data Available"
          description="No city data is available for the selected filters."
        />
      )
    }

    const kpis = calculateKPIs(processedData)
    const insights = showInsights ? generateInsights(processedData, t) : []

    return (
      <PrintWrapper>
        <Box marginBottom="30px">
          <StatsContainer
            fullWidth
            stats={[
              {
                id: 'total-cities',
                label: 'Cities Analyzed',
                value: kpis.totalCities.toString(),
                variant: 'default',
              },
              {
                id: 'avg-price-sqm',
                label: isRental ? 'Average Rent/m²' : 'Average Price/m²',
                value: formatPricePerSqm(kpis.avgPricePerSqm),
                variant: 'default',
              },
              {
                id: 'highest-city',
                label: 'Most Expensive City',
                value: kpis.highestCity ? kpis.highestCity.city : 'N/A',
                variant: 'default',
              },
              {
                id: 'total-listings',
                label: 'Total Listings',
                value: kpis.totalListings.toLocaleString(),
                variant: 'default',
              },
            ]}
          />
        </Box>

        <ChartSection>
          <Box marginBottom="24px">
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap="16px"
            >
              <ChartTitle>
                {isRental ? 'Rent' : 'Price'} Density Patterns Across Major
                Cities
              </ChartTitle>
              <Box
                display="flex"
                gap="16px"
                flexWrap="wrap"
                alignItems="center"
              >
                {showControls && (
                  <ButtonGroup>
                    {VIEW_TYPES.map((vt) => (
                      <Button
                        key={vt.type}
                        variant={viewType === vt.type ? 'primary' : 'secondary'}
                        size="small"
                        onClick={() => setViewType(vt.type)}
                      >
                        {vt.label}
                      </Button>
                    ))}
                  </ButtonGroup>
                )}
              </Box>
            </Box>
          </Box>
          <ChartContainer height={600} ref={chartRef} />
        </ChartSection>

        <CityAnalysisPrintWrapper>
          <Box marginTop="30px">
            <ChartSection>
              <Box marginBottom="24px">
                <ChartTitle>City Comparison Details</ChartTitle>
              </Box>
              <DataTableWrapper>
                <DataTable
                  data={getSortedData()}
                  columns={getTableColumns()}
                  sort={sortConfig}
                  onSort={handleSort}
                  pagination={undefined}
                  stickyHeader={false}
                />
              </DataTableWrapper>
            </ChartSection>
          </Box>
        </CityAnalysisPrintWrapper>

        {insights.length > 0 && (
          <Box>
            <ChartTitle style={{ marginBottom: '24px' }}>
              Key Insights
            </ChartTitle>
            <Box display="flex" flexWrap="wrap" gap="24px">
              {insights.map((insight, index) => (
                <Box
                  key={index}
                  flex="1 1 300px"
                  style={{
                    background: '#f8f9fa',
                    borderRadius: '12px',
                    padding: '20px',
                    borderLeft: `4px solid ${accentColor}`,
                  }}
                >
                  <Text
                    variant="h4"
                    style={{
                      fontSize: '16px',
                      fontWeight: 600,
                      marginBottom: '8px',
                      color: '#1a1a1a',
                    }}
                  >
                    {insight.title}
                  </Text>
                  <Text
                    variant="body2"
                    style={{
                      fontSize: '14px',
                      lineHeight: 1.5,
                      color: '#6c757d',
                    }}
                  >
                    {insight.description}
                  </Text>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </PrintWrapper>
    )
  }
)

CityAnalysisChart.displayName = 'CityAnalysisChart'
